import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@lohi/environments/environment';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { shareReplay, throttleTime } from 'rxjs/operators';
import {
  CurrentWeekResponse,
  EligibilityResponse,
  ListEligibilityOpts,
  ListLoadBonusesOpts,
  ListShiftEvaluationsOpts,
  LoadBonusesResponse,
  ProgramConfigResponse,
  ShiftEvaluationsResponse,
  SummaryOpts,
  SummaryResponse,
} from './types';

/**
 * HTTP layer for the carrier-facing PFP endpoints. State management mirrors
 * `OwnerDashboardService`: each cacheable resource lives behind a
 * `BehaviorSubject` shared via `shareReplay(1)`, refreshed when consumers
 * touch the public getter (which pushes a token into a throttled Subject).
 *
 * Endpoints under `${environment.api}/v2/admin/preferred_partner` are gated
 * by `CompanyAdminMiddleware` server-side; the carrier id is derived from
 * the JWT, so the frontend never has to pass it explicitly.
 */
@Injectable({ providedIn: 'root' })
export class PreferredPartnerService {
  public get summary$(): Observable<SummaryResponse | null> {
    this.summaryThrottle$$.next(undefined);
    return this.summaryShared$;
  }

  public get currentWeek$(): Observable<CurrentWeekResponse | null> {
    this.currentWeekThrottle$$.next();
    return this.currentWeekShared$;
  }

  public get programConfig$(): Observable<ProgramConfigResponse | null> {
    this.programConfigThrottle$$.next();
    return this.programConfigShared$;
  }

  constructor(private http: HttpClient) {
    // 60s throttle on the summary feed: the owner-dashboard card pulls it,
    // and the cron only writes once a week.
    this.summaryThrottle$$.pipe(throttleTime(60_000)).subscribe((opts) => {
      this.fetchSummary(opts);
    });
    this.currentWeekThrottle$$.pipe(throttleTime(5 * 60_000)).subscribe(() => {
      this.fetchCurrentWeek();
    });
    this.programConfigThrottle$$.pipe(throttleTime(5 * 60_000)).subscribe(() => {
      this.fetchProgramConfig();
    });
  }
  private readonly base = `${environment.api}/v2/admin/preferred_partner`;

  // ---- Summary (cached + auto-refreshed) ----
  private summary$$ = new BehaviorSubject<SummaryResponse | null>(null);
  private summaryThrottle$$ = new Subject<SummaryOpts | undefined>();
  private summaryShared$ = this.summary$$.pipe(shareReplay(1));

  // ---- Current week (cached) ----
  private currentWeek$$ = new BehaviorSubject<CurrentWeekResponse | null>(null);
  private currentWeekThrottle$$ = new Subject<void>();
  private currentWeekShared$ = this.currentWeek$$.pipe(shareReplay(1));

  // ---- Program config (cached) ----
  private programConfig$$ = new BehaviorSubject<ProgramConfigResponse | null>(null);
  private programConfigThrottle$$ = new Subject<void>();
  private programConfigShared$ = this.programConfig$$.pipe(shareReplay(1));

  public refreshSummary(opts?: SummaryOpts): void {
    this.summaryThrottle$$.next(opts);
  }

  // ===== Cached fetchers =====

  private fetchSummary(opts?: SummaryOpts): void {
    let params = new HttpParams();
    if (opts?.weekStart) {
      params = params.set('weekStart', opts.weekStart);
    }
    if (opts?.regionId) {
      params = params.set('regionId', String(opts.regionId));
    }
    this.http
      .get<SummaryResponse>(`${this.base}/summary`, { params })
      .subscribe(
        (resp) => this.summary$$.next(resp),
        () => this.summary$$.next(null),
      );
  }

  private fetchCurrentWeek(): void {
    this.http.get<CurrentWeekResponse>(`${this.base}/calendar/current_week`).subscribe(
      (resp) => this.currentWeek$$.next(resp),
      () => this.currentWeek$$.next(null),
    );
  }

  private fetchProgramConfig(): void {
    this.http.get<ProgramConfigResponse>(`${this.base}/program_config`).subscribe(
      (resp) => this.programConfig$$.next(resp),
      () => this.programConfig$$.next(null),
    );
  }

  // ===== One-shot fetchers (return Observable directly) =====

  public getProgramConfig$(regionId?: number): Observable<ProgramConfigResponse> {
    let params = new HttpParams();
    if (regionId) {
      params = params.set('regionId', String(regionId));
    }
    return this.http.get<ProgramConfigResponse>(`${this.base}/program_config`, { params });
  }

  public getCurrentWeek$(): Observable<CurrentWeekResponse> {
    return this.http.get<CurrentWeekResponse>(`${this.base}/calendar/current_week`);
  }

  public listEligibility$(opts: ListEligibilityOpts = {}): Observable<EligibilityResponse> {
    let params = new HttpParams();
    if (opts.weeks != null) {
      params = params.set('weeks', String(opts.weeks));
    }
    if (opts.regionId) {
      params = params.set('regionId', String(opts.regionId));
    }
    if (opts.weekStart) {
      params = params.set('weekStart', opts.weekStart);
    }
    return this.http.get<EligibilityResponse>(`${this.base}/eligibility`, { params });
  }

  public listShiftEvaluations$(opts: ListShiftEvaluationsOpts): Observable<ShiftEvaluationsResponse> {
    let params = new HttpParams().set('weekStart', opts.weekStart);
    if (opts.driverEligibleAtClose != null) {
      params = params.set('driverEligibleAtClose', String(opts.driverEligibleAtClose));
    }
    if (opts.regionId) {
      params = params.set('regionId', String(opts.regionId));
    }
    if (opts.limit) {
      params = params.set('limit', String(opts.limit));
    }
    if (opts.offset) {
      params = params.set('offset', String(opts.offset));
    }
    return this.http.get<ShiftEvaluationsResponse>(`${this.base}/shift_evaluations`, { params });
  }

  public listLoadBonuses$(opts: ListLoadBonusesOpts = {}): Observable<LoadBonusesResponse> {
    let params = new HttpParams();
    if (opts.weekStart) {
      params = params.set('weekStart', opts.weekStart);
    }
    if (opts.bonusType) {
      params = params.set('bonusType', opts.bonusType);
    }
    if (opts.includeArchived != null) {
      params = params.set('includeArchived', String(opts.includeArchived));
    }
    if (opts.driverId) {
      params = params.set('driverId', String(opts.driverId));
    }
    if (opts.regionId) {
      params = params.set('regionId', String(opts.regionId));
    }
    if (opts.limit) {
      params = params.set('limit', String(opts.limit));
    }
    if (opts.offset) {
      params = params.set('offset', String(opts.offset));
    }
    return this.http.get<LoadBonusesResponse>(`${this.base}/load_bonuses`, { params });
  }

  public getSummary$(opts: SummaryOpts = {}): Observable<SummaryResponse> {
    let params = new HttpParams();
    if (opts.weekStart) {
      params = params.set('weekStart', opts.weekStart);
    }
    if (opts.regionId) {
      params = params.set('regionId', String(opts.regionId));
    }
    return this.http.get<SummaryResponse>(`${this.base}/summary`, { params });
  }
}
